import tablesMetadataService from '../services/tablesMetadataService';

/**
 * Get the mapping string for a specific field in a table
 * @param {string} tableName - The name of the table
 * @param {string} fieldName - The name of the field
 * @returns {string|null} The mapping string or null if not found
 */
export const getMappingStringFromTable = (tableName, fieldName) => {
  const fields = tablesMetadataService.tableFields(tableName);
  if (!fields) return null;
  
  const field = fields.find(f => f.name === fieldName);
  return field?.mapping || null;
};

/**
 * Get a human-readable text value from a mapping based on an ID
 * @param {number|string} value - The ID value to look up
 * @param {string} mappingString - The mapping string in format "id1=text1;id2=text2"
 * @param {string} defaultText - Default text to return if no mapping is found
 * @returns {string} The mapped text value or defaultText
 */
export const getTextFromMapping = (value, mappingString, defaultText = 'Unknown') => {
  if (value === null || value === undefined) return defaultText;
  if (!mappingString) return defaultText;
  
  const mappings = tablesMetadataService.parseMappings(mappingString);
  const mapping = mappings.find(m => m.id === parseInt(value));
  return mapping ? mapping.text : defaultText;
};

/**
 * Get a human-readable text value from a table field mapping
 * @param {number|string} value - The ID value to look up
 * @param {string} tableName - The name of the table
 * @param {string} fieldName - The name of the field
 * @param {string} defaultText - Default text to return if no mapping is found
 * @returns {string} The mapped text value or defaultText
 */
export const getTextFromTableMapping = (value, tableName, fieldName, defaultText = 'Unknown') => {
  const mappingString = getMappingStringFromTable(tableName, fieldName);
  return getTextFromMapping(value, mappingString, defaultText);
};

/**
 * Get all mappings as an array of {id, text} objects
 * @param {string} mappingString - The mapping string in format "id1=text1;id2=text2"
 * @returns {Array} Array of {id, text} objects
 */
export const getMappingsArray = (mappingString) => {
  if (!mappingString) return [];
  return tablesMetadataService.parseMappings(mappingString);
};

/**
 * Get all mappings from a table field as an array of {id, text} objects
 * @param {string} tableName - The name of the table
 * @param {string} fieldName - The name of the field
 * @returns {Array} Array of {id, text} objects
 */
export const getTableMappingsArray = (tableName, fieldName) => {
  const mappingString = getMappingStringFromTable(tableName, fieldName);
  return getMappingsArray(mappingString);
};

/**
 * Convert mappings to options format for select inputs
 * @param {string} mappingString - The mapping string in format "id1=text1;id2=text2"
 * @returns {Array} Array of {value, label} objects for select inputs
 */
export const getMappingsAsOptions = (mappingString) => {
  if (!mappingString) return [];
  const mappings = tablesMetadataService.parseMappings(mappingString);
  return mappings.map(item => ({
    value: item.id,
    label: item.text
  }));
};

/**
 * Convert table field mappings to options format for select inputs
 * @param {string} tableName - The name of the table
 * @param {string} fieldName - The name of the field
 * @returns {Array} Array of {value, label} objects for select inputs
 */
export const getTableMappingsAsOptions = (tableName, fieldName) => {
  const mappingString = getMappingStringFromTable(tableName, fieldName);
  return getMappingsAsOptions(mappingString);
}; 